/**
 * YUO unified design system — one continuous space.
 *
 * Every room breathes on the same #05060c background, borrows the same
 * radial-light material and the same stone geometry. The colour of a room
 * is the ONLY thing that differs — everything else is shared.
 */

export const BG = "var(--yuo-bg)";
export const INK = "var(--yuo-ink)";
export const MUTED_60 = "var(--yuo-muted-60)";
export const MUTED_45 = "var(--yuo-muted-45)";
export const MUTED_36 = "var(--yuo-muted-36)";
export const MUTED_30 = "var(--yuo-muted-30)";
export const SURFACE_5 = "var(--yuo-surface-5)";

export const FONT = '"JetBrains Mono", ui-monospace, monospace';

// One palette per room. `rgb` is a CSS-variable triplet used inside rgba()
// (night = the original numbers, day = pastel); `hex` is the literal orb
// colour (orbs never change); `text` is the role text colour for labels.
const R = (role) => `var(--role-${role}-rgb)`;
const T = (role) => `var(--role-${role}-text)`;
const A = (role) => `var(--role-${role}-accent)`;
export const ROOMS = {
  mirror:    { rgb: R("blue"),   hex: "#4F9DFF", text: T("blue"), accent: A("blue"),   light: "rgb(207,230,255)", chip_light: "#E0EDFF", role: "blue" },
  diary:     { rgb: R("purple"), hex: "#B06BFF", text: T("purple"), accent: A("purple"), light: "rgb(230,200,255)", chip_light: "#E9DBFF", role: "purple" },
  library:   { rgb: R("yellow"), hex: "#E9C46A", text: T("yellow"), accent: A("yellow"), light: "rgb(255,238,196)", chip_light: "#FFEECC", role: "yellow" },
  content:   { rgb: R("coral"),  hex: "#FF7A66", text: T("coral"), accent: A("coral"),  light: "rgb(255,205,196)", chip_light: "#FFD7CE", role: "coral" },
  symbiosis: { rgb: R("green"),  hex: "#34FF96", text: T("green"), accent: A("green"),  light: "rgb(180,255,215)", chip_light: "#C9FFDF", role: "green" },
  // Tool palettes inside Content
  screen:    { rgb: R("blue"),   hex: "#4F9DFF", text: T("blue"), accent: A("blue"),   role: "blue" },
  planning:  { rgb: R("green"),  hex: "#34FF96", text: T("green"), accent: A("green"),  role: "green" },
  mirdiary:  { rgb: R("purple"), hex: "#B06BFF", text: T("purple"), accent: A("purple"), role: "purple" },
  expression:{ rgb: R("coral"),  hex: "#FF7A66", text: T("coral"), accent: A("coral"),  role: "coral" },
  // Feeling / craft / growth accents
  feeling:   { hex: "#FF9D8F", rgb: R("feeling"), role: "coral", accent: A("coral") },
  craft:     { hex: "#C3B9F5", rgb: R("craft"),   role: "purple", accent: A("purple") },
  growth:    { hex: "#8AFFC4", rgb: R("growth"),  role: "green", accent: A("green") },
};
const RGB_TO_ROLE = Object.fromEntries(Object.values(ROOMS).map((m) => [m.rgb, m.role]));

// Global keyframes. Injected exactly once by <YuoStyles/>.
export const YUO_KEYFRAMES = `
@keyframes yuo-emerge {
  from { opacity: 0; filter: blur(14px); transform: translateY(14px) scale(.985); }
  to   { opacity: 1; filter: blur(0);    transform: none; }
}
@keyframes yuo-breathe {
  0%, 100% { opacity: .5; }
  50%      { opacity: 1; }
}
@keyframes yuo-drift {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(2%, -3%, 0) scale(1.08); }
}
@keyframes yuo-spin { to { transform: rotate(360deg); } }
::-webkit-scrollbar { width: 0; height: 0; }
* { scrollbar-width: none; }
button, a { outline: none !important; -webkit-tap-highlight-color: transparent; }
button:focus, a:focus, button:focus-visible, a:focus-visible { outline: none !important; box-shadow: inherit; }
`;

// -- Layer builders --------------------------------------------------------
// Ambient / nebula for a "generic" room (Mirror / Diary / Content).
export function ambientBackground(rgb) {
  return (
    `radial-gradient(60% 45% at 18% 4%, rgba(${rgb},0.14), transparent 72%),` +
    `radial-gradient(55% 40% at 82% 6%, rgba(52,255,150,0.10), transparent 72%),` +
    `radial-gradient(90% 55% at 50% 112%, rgba(${rgb},0.12), transparent 72%)`
  );
}
export function nebulaBackground(rgb) {
  return `radial-gradient(44% 32% at 50% 16%, rgba(${rgb},0.11), rgba(${rgb},0.035) 56%, transparent 90%)`;
}
// Library is different — green halo above the YUO stone, warm amber below
export const AMBIENT_LIBRARY =
  `radial-gradient(30% 23% at 50% 11%, rgba(52,255,150,0.11), rgba(52,255,150,0.03) 50%, transparent 86%),` +
  `radial-gradient(92% 50% at 50% 96%, rgba(233,196,106,0.11), rgba(233,196,106,0.035) 55%, transparent 90%)`;
export const NEBULA_LIBRARY =
  `radial-gradient(40% 27% at 50% 13%, rgba(52,255,150,0.07), rgba(52,255,150,0.02) 52%, transparent 88%)`;

// -- Card / bubble backgrounds --------------------------------------------
// Message glow (used for cards, tiles, bubbles).
export function glow(rgb, alpha = 0.16) {
  return `radial-gradient(120% 100% at 22% 0%, rgba(${rgb},${alpha}), rgba(${rgb},0.03) 62%, transparent 100%)`;
}
// User bubble (right-aligned) — glow from top-right corner.
export function bubbleRight(rgb, alpha = 0.22) {
  return `radial-gradient(120% 140% at 70% 0%, rgba(${rgb},${alpha}), rgba(${rgb},0.05) 65%, transparent 100%)`;
}

// -- Stone gradient builders ----------------------------------------------
// Small orb — used everywhere from tab icons to input dots.
export function stoneGradient({ light, mid, dark }) {
  return `radial-gradient(38% 34% at 34% 26%, ${light} 0%, ${mid} 20%, ${dark} 60%, rgb(3,6,12) 100%)`;
}
export const STONE_GRADIENTS = {
  mirror:   stoneGradient({ light: "#FFFFFF", mid: "#4F9DFF", dark: "#12407F" }),
  diary:    stoneGradient({ light: "#FFFFFF", mid: "#B06BFF", dark: "#3A1470" }),
  library:  stoneGradient({ light: "#FFFFFF", mid: "#E9C46A", dark: "#7A5A20" }),
  content:  stoneGradient({ light: "#FFFFFF", mid: "#FF7A66", dark: "#7A2418" }),
  symbiosis:stoneGradient({ light: "#FFFFFF", mid: "#4DFFA0", dark: "#0D3A26" }),
};

// -- Capsule (pill button) --------------------------------------------------
export function capsuleStyle({ active, room = "mirror", size = "md", rgb: rgbOverride, idleTint = false } = {}) {
  const rgb = rgbOverride || ROOMS[room]?.rgb || ROOMS.mirror.rgb;
  const role = RGB_TO_ROLE[rgb] || null;
  const pads = {
    sm: { pad: "6px 14px", font: 9.5 },
    md: { pad: "8px 18px", font: 10   },
    lg: { pad: "10px 22px", font: 11  },
  }[size] || { pad: "8px 18px", font: 10 };
  return {
    borderRadius: 999,
    padding: pads.pad,
    fontSize: pads.font,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    fontFamily: FONT,
    fontWeight: 500,
    color: active ? (role ? `var(--role-${role}-text)` : INK) : "var(--yuo-pill-text)",
    background: active
      ? (role ? `var(--role-${role}-pill-bg)` : `radial-gradient(120% 160% at 30% 0%, rgba(${rgb},0.32), rgba(${rgb},0.10))`)
      : (idleTint ? `linear-gradient(rgba(${rgb},var(--pill-idle-a)), rgba(${rgb},var(--pill-idle-a))), var(--yuo-pill-bg)` : "var(--yuo-pill-bg)"),
    boxShadow: active
      ? (role ? `var(--role-${role}-pill-glow)` : `0 0 24px rgba(${rgb},0.28)`)
      : "var(--yuo-pill-glow)",
    border: "none",
    cursor: "pointer",
    transition: "filter 200ms",
    display: "inline-flex", alignItems: "center", gap: 8,
  };
}

// tint("var(--role-blue-accent)", "22") → rgba(var(--role-blue-rgb),0.13); literal hex → "#RRGGBB22"
export function tint(color, hexAlpha) {
  const m = /^var\(--(?:role|lit)-(\w+?)-(?:accent|text|\d+)\)$/.exec(color || "");
  if (m) return `rgba(var(--role-${m[1]}-rgb),${(parseInt(hexAlpha, 16) / 255).toFixed(2)})`;
  return `${color}${hexAlpha}`;
}
